import React, { useEffect, useMemo, useRef, useState } from "react";
import Isotope from "isotope-layout";
import ProjectDetailsModal from "./ProjectDetailsModal";

const Portfolio = () => {
  const isotope = useRef();
  const [filterKey, setFilterKey] = useState("*");
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState();
  const [isOpen, setIsOpen] = useState(false);

  const htmlElement = document.getElementsByTagName("html")[0];
  const isRtl = htmlElement.getAttribute("dir") === "rtl";

  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isTehTimeZone = localTimeZone === "Asia/Tehran";

  const filters = {
    DETAILED: "Developed projects",
    MOCKUPS: "Prototype Designs by Team",
  };

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
  };

  const sharedEngineeringStandards =
    "All modules are implemented in TypeScript, exposed through RESTful APIs documented in Postman, and shipped with CI/CD automation on GitHub. Deployment is containerized with Docker Compose + Nginx, and production hardening includes OTP/JWT auth controls, RBAC for admin actions, input validation/sanitization, encrypted transport (HTTPS), security headers, and structured audit logging.";

  const globalProjects = [
    {
      title: "Pulsing (Goal-based Social Connection App)",
      slug: "pulsing",
      slugAliases: ["pulsing-demo"],
      type: types.DOCUMENT,
      previewLayout: "desktop",
      status: "Launched and delivered",
      role: "Product design and development with the Pulsing team",
      document: {
        challenge:
          "Starting a meaningful connection around a shared goal, time, and place can feel scattered; users need a clear path to find the right people.",
        outcome:
          "The iOS and Android apps were launched, enabling people to create pulses, find others with shared goals, and start a conversation.",
        projectInfo:
          "Pulsing is a goal-based social app that connects people around a shared goal, time, and place. The product has been built and delivered, with iOS and Android releases available to download.",
        client: "Pulsing Product Team",
        technologies: "SwiftUI, Kotlin, TypeScript, React, Node.js",
        industry: "iOS and Android social app",
        date: "2026",
        urls: [
          {
            name: "Official site",
            link: "https://pulsing.ir",
          },
          {
            name: "iOS - App Store",
            link: "https://apps.apple.com/app/id6803828017",
          },
          {
            name: "Android - Cafe Bazaar",
            link: "https://cafebazaar.ir/app/com.app.pulsing",
          },
        ],
        sliderImages: [
          "images/projects/pulsing/screenshot-launched.png",
        ],
      },
      thumbImage: "images/projects/pulsing/main-launched.png",
      categories: [filters.DETAILED],
    },
    {
      title: "SimpleTemp (Mobile and Web-based Hiring Platform)",
      slug: "simpletemp-demo",
      type: types.DOCUMENT,
      previewLayout: "desktop",
      status: "Delivered",
      role: "Lead Developer, Project Manager, and Contractor",
      document: {
        challenge:
          "Dental clinics and professionals needed a faster, clearer way to manage temporary staffing and connect directly.",
        outcome:
          "A web and mobile hiring platform was delivered to simplify temporary staffing and connect clinics with dental professionals.",
        projectInfo:
          "SimpleTemp is a modern platform for dental clinics, hygienists, and dental nurses in London, UK. This application simplifies the temporary staffing process by directly connecting clinics with professionals in the dental field. I was the lead developer for iOS, backend, and frontend, while the Android version was developed by another team member. Additionally, I was the project manager and the main contractor with the client.",
        client: "Soheil Kamali",
        technologies:
          "TypeScript, iOS, Android, Node.js, React, MongoDB, Swift, Kotlin, Stripe, Firebase",
        industry: "Web & Mobile App Development",
        date: "July 16, 2024",
        urls: [
          {
            name: "Offical Site",
            link: "http://simpletemp.co.uk",
          },
          {
            name: "iOS - App Store",
            link: "https://apps.apple.com/gb/app/simpletemp/id1631831946",
          },
          {
            name: "Android - Google Play",
            link: "https://play.google.com/store/apps/details?id=com.simpletempco.simpletemp&gl=UK",
          },
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/simpletemp-demo",
          },
        ],

        sliderImages: [
          "images/projects/simpletemp/sc4-01.png",
          "images/projects/simpletemp/sc4-02.png",
          "images/projects/simpletemp/1.png",
          "images/projects/simpletemp/2.png",
          "images/projects/simpletemp/3.png",
          "images/projects/simpletemp/4.png",
          "images/projects/simpletemp/5.png",
          "images/projects/simpletemp/6.png",
          "images/projects/simpletemp/7.png",
          "images/projects/simpletemp/8.png",
        ],
      },

      thumbImage: "images/projects/simpletemp/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "JobLoyal (Platform for Connecting Employers and Service Workers)",
      slug: "jobloyal-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      status: "Delivered",
      role: "Backend and native mobile development",
      document: {
        challenge:
          "Home-service bookings needed fast, real-time coordination between customers and service providers.",
        outcome:
          "A real-time service marketplace was developed to connect customers with service providers and bring booking and service management into one product.",
        projectInfo:
          "Jobloyal is a real-time home services marketplace application, available for both iOS and Android as native applications. The platform connects users (customers) with service providers (jobbers), enabling seamless service booking and management. The backend is powered by Node.js with a PostgreSQL database, ensuring high scalability and efficiency.",
        client: "Mr. Serkan",
        technologies:
          "TypeScript, iOS, Android, Node.js, React, MongoDB, Swift, Kotlin, Firebase",
        industry: "Web & Mobile App Development",
        date: "October 16, 2022",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/jobloyal-demo",
          },
        ],

        sliderImages: [
          "images/projects/jobloyal/sc4-01.png",
          "images/projects/jobloyal/sc4-02.png",
          "images/projects/jobloyal/1.jpg",
          "images/projects/jobloyal/2.jpg",
          "images/projects/jobloyal/3.jpg",
          "images/projects/jobloyal/4.jpg",
          "images/projects/jobloyal/5.jpg",
          "images/projects/jobloyal/6.jpg",
        ],
      },

      thumbImage: "images/projects/jobloyal/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Radkit (IoT-based Home Automation System)",
      slug: "radbms-demo",
      slugAliases: ["radkit-demo"],
      type: types.DOCUMENT,
      previewLayout: "mobile",
      status: "Delivered",
      role: "iOS development and device communication",
      document: {
        challenge:
          "Smart-home controls needed to work reliably over both local networks and internet connections.",
        outcome:
          "An iOS application was delivered with device control over TCP, UDP, and MQTT.",
        projectInfo:
          "The Radkit app is an intelligent solution for controlling home appliances and connected objects over a local network and the internet. This project is developed using Swift in the Xcode environment. The app allows device control through both Local Network (TCP/UDP) and MQTT.",
        client: "Radbms co.",
        technologies: "TypeScript, iOS, Swift, UIKit, MQTT, TCP, UDP, IoT, Xcode",
        industry: "Mobile App Development",
        date: "October 20, 2024",
        urls: [
          {
            name: "iOS - App Store",
            link: "https://apps.apple.com/us/app/radbms/id1599735488",
          },
          {
            name: "Android - APK",
            link: "https://imaxbms.com/files",
          },
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/radkit-demo",
          },
        ],

        sliderImages: [
          "images/projects/radkit/sc4-01.png",
          "images/projects/radkit/sc4-02.png",
          "images/projects/radkit/1.png",
          "images/projects/radkit/2.png",
          "images/projects/radkit/3.png",
          "images/projects/radkit/4.png",
          "images/projects/radkit/5.png",
          "images/projects/radkit/6.png",
          "images/projects/radkit/7.png",
          "images/projects/radkit/8.png",
        ],
      },

      thumbImage: "images/projects/radkit/main.png",
      categories: [filters.DETAILED],
    },
    {
      title:
        "I Heart Saving (E-commerce App for Local Shopping and Exclusive Discounts)",
      slug: "iheartsaving-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      status: "Demo",
      role: "iOS application development",
      document: {
        projectInfo:
          "I Heart Saving is a demo e-commerce app showcasing a yearly membership model for exclusive discounts and offers. It promotes local shopping while helping users save significantly.",
        client: "iHeartSaving co.",
        technologies: "TypeScript, iOS, Swift, UIKit, Xcode, In-App Purchases",
        industry: "Mobile App Development",
        date: "Sep 16, 2019",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/iheartsaving-demo",
          },
        ],

        sliderImages: [
          "images/projects/iHeartsaving/1.png",
          "images/projects/iHeartsaving/2.png",
          "images/projects/iHeartsaving/3.png",
          "images/projects/iHeartsaving/4.png",
          "images/projects/iHeartsaving/5.png",
        ],
      },

      thumbImage: "images/projects/iHeartsaving/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Emmanuel (Online Confectionery Store)",
      slug: "emmanuel-demo",
      type: types.DOCUMENT,
      previewLayout: "desktop",
      status: "Demo",
      role: "Web and mobile development",
      document: {
        projectInfo:
          "Emmanuel is an online confectionery store based in the United Kingdom, designed to provide a seamless shopping experience for sweet lovers. This project is a demo application, showcasing a fully functional e-commerce platform with modern features for browsing, comparing, and purchasing confectionery products.",
        client: "Emmanuel co.",
        technologies:
          "TypeScript, Node.js, React, PostgreSQL, Express, Redux, JWT, Material UI, Stripe, Swift, iOS Development, Android Development",
        industry: "Mobile App Development",
        date: "August 16, 2019",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/emmanuel-demo",
          },
        ],

        sliderImages: [
          "images/projects/emmanuel/1.jpg",
          "images/projects/emmanuel/2.jpg",
          "images/projects/emmanuel/3.jpg",
          "images/projects/emmanuel/4.jpg",
          "images/projects/emmanuel/5.jpg",
          "images/projects/emmanuel/6.jpg",
          "images/projects/emmanuel/7.jpg",
        ],
      },

      thumbImage: "images/projects/emmanuel/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "HonistAI (Telegram-based AI Chat Super App for iOS)",
      slug: "honistai-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "HonistAI is an advanced unofficial Telegram client inspired by Nicegram and built on Telegram iOS v12 architecture. The iOS app is developed with Swift + UIStoryboard (iOS 26), and extends messaging with an OpenAI-powered assistant layer that behaves similarly to ChatGPT inside the app. Admin-defined AI assistants and prompt templates are managed from the backend panel and exposed dynamically to end-users. Apple In-App Purchase subscriptions and one-time packs are monetized through a GEM economy (for example, $5 converts to 200 GEM), then consumed based on AI token usage. The platform also supports Google Ads placements inside conversation flows with admin-side configuration controls. This release is in pre-launch stage with no public URL yet. " +
          sharedEngineeringStandards,
        client: "HonistAI Product Team",
        technologies:
          "TypeScript, Swift, UIStoryboard, iOS 26, Node.js, PostgreSQL, OpenAI API, Apple In-App Purchase, Google Ads, Firebase",
        industry: "AI Messaging & Mobile Platform",
        date: "May 2025",
        urls: [
          {
            name: "Status",
            link: "https://sinakhanjani.com/honistai-demo",
          },
        ],
        sliderImages: [
          "images/projects/honistai/1.png",
          "images/projects/honistai/2.png",
          "images/projects/honistai/3.png",
          "images/projects/honistai/4.png",
          "images/projects/honistai/5.png",
          "images/projects/honistai/6.png",
          "images/projects/honistai/7.png",
        ],
      },
      thumbImage: "images/projects/honistai/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "VegalyX (30-Second BTC Prediction & Settlement Platform)",
      slug: "vegalyx-demo",
      type: types.DOCUMENT,
      previewLayout: "desktop",
      document: {
        projectInfo:
          "VegalyX is a high-frequency web trading platform focused on 30-second Bitcoin prediction rounds. Users predict whether price will close higher or lower; losing-side liquidity is automatically distributed to winning positions. The entire stack is built with TypeScript (Node.js + PostgreSQL backend, React PWA frontend, React admin dashboard) and engineered for real-time behavior using Socket.IO across wallet balance updates, on-chain payments, online chat, leaderboard movements, market ticks, and settlement events. BTC pricing is ingested from Binance and rebroadcast in real time. Wallet deposit/withdraw operations integrate with Tatum and support TRC20/ERC20/BEP20 auto-processing. The platform enforces ACID transaction design for financial integrity and uses Firebase for managed push notifications. Infrastructure is deployed on Google Cloud with containerized delivery and automated GitHub CI/CD pipelines. " +
          sharedEngineeringStandards,
        client: "VegalyX Product Team",
        technologies:
          "TypeScript, Node.js, PostgreSQL, React, PWA, Socket.IO, Binance Market Data, Tatum, Firebase, Google Cloud, Docker, Nginx",
        industry: "FinTech / Crypto Trading Platform",
        date: "May 2025",
        urls: [
          {
            name: "Official Site",
            link: "https://vegalyx.com",
          },
          {
            name: "Live Demo",
            link: "https://demo.vegalyx.com",
          },
        ],
        sliderImages: [
          "images/projects/vegalyx/1.png",
          "images/projects/vegalyx/2.jpg",
          "images/projects/vegalyx/3.png",
          "images/projects/vegalyx/4.png",
          "images/projects/vegalyx/5.png",
          "images/projects/vegalyx/6.png",
          "images/projects/vegalyx/7.png",
          "images/projects/vegalyx/8.png",
          "images/projects/vegalyx/9.png",
          "images/projects/vegalyx/10.png",
          "images/projects/vegalyx/11.png",
        ],
      },
      thumbImage: "images/projects/vegalyx/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Talkyo (Autonomous Social DM AI Agent Platform)",
      slug: "talkyo-demo",
      type: types.DOCUMENT,
      previewLayout: "desktop",
      document: {
        projectInfo:
          "Talkyo is an in-progress SaaS platform that builds autonomous AI agents for social inboxes (Instagram/Facebook DMs and similar channels). The MVP is powered by OpenAI APIs and starts with an automated self-learning pipeline: once a business account is connected, the system crawls posts/reels/profile metadata through social Graph APIs, builds structured business context, and generates response behavior without forcing users to write complex prompts manually. The runtime AI assistant then handles incoming direct messages with contextual intent routing, safe fallback policies, and handoff-ready escalation logic. The architecture is designed for retrieval-first intelligence (RAG + embeddings + vector search) with optional fine-tuning tracks for high-volume accounts where domain style-lock is required. Multi-tenant orchestration, observability, guardrails, and cost-aware prompt pipelines are core design priorities for the next milestones. " +
          sharedEngineeringStandards,
        client: "Talkyo Product Team",
        technologies:
          "TypeScript, Node.js, PostgreSQL, React, OpenAI API, Vector Search, RAG, Instagram/Facebook Graph API, Webhooks, Docker, Nginx",
        industry: "AI Automation / Social Commerce",
        date: "2026 (In Development)",
        urls: [
          {
            name: "Main Site",
            link: "https://talkyo.app",
          },
          {
            name: "User Dashboard",
            link: "https://my.talkyo.app",
          },
        ],
        sliderImages: [
          "images/projects/talkyo/1.png",
          "images/projects/talkyo/2.png",
          "images/projects/talkyo/3.png",
          "images/projects/talkyo/4.png",
          "images/projects/talkyo/5.png",
          "images/projects/talkyo/6.png",
          "images/projects/talkyo/7.png",
        ],
      },
      thumbImage: "images/projects/talkyo/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Paira (Subscription-based iOS Matchmaking Platform)",
      slug: "paira-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Paira is a modern matchmaking platform with a Node.js backend, React admin dashboard, and Next.js web frontend, combined with a native iOS 26 application. The monetization model includes Apple In-App Purchase subscriptions and one-time packs (such as extra likes) to unlock premium interactions. Real-time messaging is implemented with Firebase Realtime Database in the iOS app, while identity and authorization controls remain anchored in the backend. The product also includes advanced matchmaking scoring algorithms for better candidate ranking and Firebase push notifications for engagement loops. Current public access is limited to the website shell while the iOS build is being distributed through TestFlight. " +
          sharedEngineeringStandards,
        client: "Paira Product Team",
        technologies:
          "TypeScript, Node.js, PostgreSQL, React, Next.js, Swift (iOS 26), Apple In-App Purchase, Firebase Realtime Database, Firebase Cloud Messaging",
        industry: "Social Networking / Dating App",
        date: "2026",
        urls: [
          {
            name: "Website",
            link: "https://paira.me",
          },
        ],
        sliderImages: [
          "images/projects/paira/1.png",
          "images/projects/paira/2.png",
          "images/projects/paira/4.png",
          "images/projects/paira/5.jpeg",
          "images/projects/paira/6.jpeg",
          "images/projects/paira/7.jpeg",
          "images/projects/paira/8.png",
          "images/projects/paira/9.png",
          "images/projects/paira/10.png",
          "images/projects/paira/11.png",
          "images/projects/paira/12.png",
          "images/projects/paira/13.png",
          "images/projects/paira/14.jpeg",
        ],
      },
      thumbImage: "images/projects/paira/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Women Health (Web & App)",
      type: types.IMAGE,
      document: {
        designer: "Mr. Aliabadi (Team UI/UX Designer)",
        industry: "Web & App Design",
        urls: [
          {
            name: "Mr. Aliabadi Profile",
            link: "https://dribbble.com/Hoseinaliabadi",
          },
        ],

        sliderImages: ["images/projects/hossein/healthapp-mobile.jpg"],
      },
      thumbImage: "images/projects/hossein/healthapp.jpg",

      categories: [filters.MOCKUPS],
    },
    {
      title: "Online Taxi Services",
      type: types.IMAGE,
      document: {
        designer: "Mr. Aliabadi (Team UI/UX Designer)",
        industry: "App Design",
        urls: [
          {
            name: "Mr. Aliabadi Profile",
            link: "https://dribbble.com/Hoseinaliabadi",
          },
        ],

        sliderImages: [],
      },
      thumbImage: "images/projects/hossein/online-taxi.jpg",

      categories: [filters.MOCKUPS],
    },
  ];

  const persianOnlyProjects = [
    {
      title: "Mahzareghanoon (Iranian Lawbook Super App)",
      slug: "mahzareghanoon-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Mahzareghanoon is a comprehensive Iranian legal book platform with a Node.js backend, PostgreSQL database, and Flutter applications for iOS and Android. The system supports OTP-based authentication, Firebase notifications, text highlighting, and unlimited legal-content categorization managed through a React admin dashboard. One of the key technical capabilities is offline-first legal delivery: backend-defined legal libraries are packaged as compressed database bundles, delivered to the app, extracted into a local database, and automatically synchronized on incremental updates so users always have an up-to-date offline reference. " +
          sharedEngineeringStandards,
        client: "Mahzareghanoon Team",
        technologies:
          "TypeScript, Node.js, PostgreSQL, Flutter (iOS/Android), React Admin, Firebase, OTP Auth, Offline Sync Engine",
        industry: "LegalTech / Mobile Knowledge Platform",
        date: "2025",
        urls: [
          {
            name: "Official Site",
            link: "https://mahzareghanoon.com",
          },
        ],
        sliderImages: [
          "images/projects/mahzareghanoon/1.jpg",
          "images/projects/mahzareghanoon/2.jpg",
          "images/projects/mahzareghanoon/3.jpg",
          "images/projects/mahzareghanoon/4.jpg",
          "images/projects/mahzareghanoon/5.jpg",
          "images/projects/mahzareghanoon/6.jpg",
          "images/projects/mahzareghanoon/7.jpg",
          "images/projects/mahzareghanoon/8.png",
          "images/projects/mahzareghanoon/9.png",
          "images/projects/mahzareghanoon/10.jpg",
          "images/projects/mahzareghanoon/12.png",
          "images/projects/mahzareghanoon/13.jpg",
          "images/projects/mahzareghanoon/14.jpg",
          "images/projects/mahzareghanoon/15.jpg",
          "images/projects/mahzareghanoon/16.jpg",
          "images/projects/mahzareghanoon/17.jpg",
        ],
      },
      thumbImage: "images/projects/mahzareghanoon/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "OSXVPN VPN",
      slug: "osxvpn-demo",
      type: types.DOCUMENT,
      previewLayout: "desktop",
      document: {
        projectInfo:
          "OSXVPN is a fully automated VPN subscription platform developed entirely in JavaScript, with a Node.js backend and a PostgreSQL database. The frontend is built using React.js, providing users with a seamless experience. This project is the result of my solo development efforts, covering both frontend and backend, as well as server-side configurations.",
        client: "osxvpn group.",
        technologies:
          "node.js, react, ibsng, postgresql, express, jwt, L2TP/IPSec, openVPN, Cisco VPN, restful API",
        industry: "Web Development",
        date: "Nov 16, 2023",
        urls: [
          {
            name: "Official Site",
            link: "https://osxnetwork.top",
          },
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/osxvpn-demo",
          },
        ],

        sliderImages: [],
      },

      thumbImage: "images/projects/osxvpn/main.svg",
      categories: [filters.DETAILED],
    },
    {
      title: "Ostovaneh",
      slug: "ostovaneh-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Ostovaneh is a cross-platform multimedia library application that allows users to access encrypted audio books, video content, and text-based books. This application ensures secure content delivery by encrypting data on the server-side and decrypting it within the application upon retrieval via API requests.",
        client: "Mr. Tofighi",
        technologies:
          "node.js, react, postgresql, express.js, jwt, iOS Development, Swift, XCode",
        industry: "Mobile App Development",
        date: "March 16, 2021",
        urls: [
          {
            name: "iOS - Sibirani",
            link: "https://sibirani.com/apps/Ostovaneh/",
          },
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/ostovaneh-demo",
          },
        ],

        sliderImages: [
          "images/projects/ostovaneh/2.jpg",
          "images/projects/ostovaneh/3.jpg",
          "images/projects/ostovaneh/4.jpg",
          "images/projects/ostovaneh/5.jpg",
          "images/projects/ostovaneh/6.jpg",
          "images/projects/ostovaneh/7.jpg",
          "images/projects/ostovaneh/8.jpg",
        ],
      },

      thumbImage: "images/projects/ostovaneh/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Course App",
      type: types.IMAGE,
      document: {
        designer: "Mr. Aliabadi (Team UI/UX Designer)",
        industry: "Web Material Design",
        urls: [
          {
            name: "Mr. Aliabadi Profile",
            link: "https://dribbble.com/Hoseinaliabadi",
          },
        ],

        sliderImages: [],
      },
      thumbImage: "images/projects/hossein/smartai.jpg",

      categories: [filters.MOCKUPS],
    },
    {
      title: "Glasor (Streamline communication between students & teachers)",
      slug: "glasor-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Glasor is a mobile application designed to streamline communication between students, teachers, and parents. Developed as a native mobile app for iOS and Android, with a backend powered by Node.js, Glasor enhances the educational experience by improving real-time interactions and accessibility of academic information.",
        client: "Mr. Ameri",
        technologies:
          "python, next.js, postgresql, django, jwt, iOS Development, Android Development, Kotlin, Swift, XCode, restful API",
        industry: "Mobile App Development",
        date: "March 16, 2020",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/glasor-demo",
          },
        ],

        sliderImages: [
          "images/projects/glasor/1.jpeg",
          "images/projects/glasor/2.jpeg",
          "images/projects/glasor/3.jpeg",
          "images/projects/glasor/4.jpeg",
          "images/projects/glasor/5.jpeg",
          "images/projects/glasor/6.jpeg",
        ],
      },

      thumbImage: "images/projects/glasor/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Cario (Your Personal Car Assistant)",
      slug: "cario-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Cario is a mobile application designed to assist car owners with their daily vehicle-related tasks effortlessly. Imagine having a digital assistant dedicated to making car maintenance and service more manageable.",
        client: "Mr. Rahimi",
        technologies:
          "Python, Django, React, postgresql, jwt, iOS Development, Android Development, Kotlin, Swift, Google Map",
        industry: "Mobile App Development",
        date: "Dec 30, 2017",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/cario-demo",
          },
        ],

        sliderImages: [
          "images/projects/cario/1.jpg",
          "images/projects/cario/2.jpg",
          "images/projects/cario/3.jpg",
          "images/projects/cario/4.jpg",
          "images/projects/cario/5.jpg",
        ],
      },

      thumbImage: "images/projects/cario/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Academy App",
      type: types.IMAGE,
      document: {
        designer: "Mr. Aliabadi (Team UI/UX Designer)",
        industry: "Web Material Design",
        urls: [
          {
            name: "Mr. Aliabadi Profile",
            link: "https://dribbble.com/Hoseinaliabadi",
          },
        ],

        sliderImages: [],
      },
      thumbImage: "images/projects/hossein/academy.jpg",

      categories: [filters.MOCKUPS],
    },
    {
      title: "Bisim App",
      slug: "bisimapp-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Bisim App is a ride-hailing mobile application, similar to Uber, that connects passengers with drivers in real time. The app allows users to request a ride by selecting their location on a map, and nearby drivers can accept the request to provide transportation. The project was initially developed for iOS and written using Swift and Xcode.",
        client: "Mr. Ranjbar",
        technologies: "socket.io, jwt, iOS Development, Swift, GSK Map, UIKit",
        industry: "Mobile App Development",
        date: "Dec 30, 2018",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/bisimappp-demo",
          },
        ],

        sliderImages: [
          "images/projects/bisimapp/1.jpeg",
          "images/projects/bisimapp/2.jpeg",
          "images/projects/bisimapp/3.jpeg",
          "images/projects/bisimapp/4.jpeg",
          "images/projects/bisimapp/5.jpeg",
          "images/projects/bisimapp/6.jpeg",
          "images/projects/bisimapp/7.jpeg",
          "images/projects/bisimapp/8.jpeg",
        ],
      },

      thumbImage: "images/projects/bisimapp/main.png",
      categories: [filters.DETAILED],
    },
    {
      title: "Course App",
      type: types.IMAGE,
      document: {
        designer: "Mr. Aliabadi (Team UI/UX Designer)",
        industry: "Web Material Design",
        urls: [
          {
            name: "Mr. Aliabadi Profile",
            link: "https://dribbble.com/Hoseinaliabadi",
          },
        ],

        sliderImages: [],
      },
      thumbImage: "images/projects/hossein/course.jpg",

      categories: [filters.MOCKUPS],
    },
    {
      title: "Nerkh",
      slug: "nerkh-demo",
      type: types.DOCUMENT,
      previewLayout: "mobile",
      document: {
        projectInfo:
          "Currency Rate Viewer is a demo application designed to provide users with real-time information on the exchange rates of various currencies. The app offers a clean and intuitive interface, allowing users to monitor, analyze, and stay updated on currency fluctuations with ease.",
        client: "Mr. Khanjani",
        technologies: "Swift, UIKit, XCode, Python, Django, Restful API",
        industry: "iOS App Development",
        date: "Feb 30, 2016",
        urls: [
          {
            name: "Github - Demo",
            link: "https://github.com/sinakhanjani/nerkh-ios-native-app",
          },
        ],

        sliderImages: [
          "images/projects/nerkh/1.jpg",
          "images/projects/nerkh/2.jpg",
          "images/projects/nerkh/3.jpg",
          "images/projects/nerkh/4.jpg",
          "images/projects/nerkh/5.jpg",
        ],
      },

      thumbImage: "images/projects/nerkh/main.png",
      categories: [filters.DETAILED],
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const projectsData = useMemo(
    () =>
      isTehTimeZone
        ? [...globalProjects, ...persianOnlyProjects]
        : globalProjects,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTehTimeZone]
  );

  const handleFilterKeyChange = (key) => () => setFilterKey(key);

  const getKeyByValue = (value) => {
    return Object.keys(filters).find((key) => filters[key] === value);
  };

  const getFilterClasses = (categories) => {
    if (categories.length > 0) {
      const tempArray = [];
      categories.forEach((category) => {
        tempArray.push(getKeyByValue(category));
      });
      return tempArray.join(" ");
    }
    return "";
  };

  const openProjectModal = (project, updateUrl = true) => {
    setSelectedProjectDetails(project);
    setIsOpen(true);
    if (updateUrl && project?.slug && window.location.pathname !== `/${project.slug}`) {
      window.history.pushState({}, "", `/${project.slug}`);
    }
  };

  const closeProjectModal = () => {
    setIsOpen(false);
    if (window.location.pathname !== "/") {
      window.history.pushState({}, "", "/");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    isotope.current = new Isotope(".portfolio-filter", {
      itemSelector: ".filter-item",
      layoutMode: "masonry",
      originLeft: !isRtl,
    });

    return () => {
      isotope.current.destroy();
    };
  }, [isRtl]);

  useEffect(() => {
    if (imagesLoaded) {
      filterKey === "*"
        ? isotope.current.arrange({ filter: `*` })
        : isotope.current.arrange({ filter: `.${filterKey}` });
    }
  }, [filterKey, imagesLoaded]);

  useEffect(() => {
    const findProjectBySlug = (slug) => {
      if (!slug) return null;
      const normalizedSlug = slug.toLowerCase();
      return projectsData.find(
        (project) =>
          project.slug === normalizedSlug ||
          project.slugAliases?.map((item) => item.toLowerCase()).includes(normalizedSlug)
      );
    };

    const openProjectFromPath = () => {
      const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
      if (!path) return;
      const matchedProject = findProjectBySlug(path);
      if (matchedProject) {
        openProjectModal(matchedProject, false);
        document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
      }
    };

    openProjectFromPath();
    window.addEventListener("popstate", openProjectFromPath);

    return () => {
      window.removeEventListener("popstate", openProjectFromPath);
    };
  }, [projectsData]);

  return (
    <>
      <section id="portfolio" className={"section bg-light"}>
        <div className={"container"}>
          <p className="text-center mb-2 wow fadeInUp">
            <span className="bg-primary text-dark px-2">Portfolio</span>
          </p>
          <h2 className="text-10 fw-600 text-center mb-3 wow fadeInUp">
            Projects you can discuss in detail
          </h2>
          <p className="text-muted text-center text-5 mb-5 wow fadeInUp">
            Some projects are delivered products, while others are demos or prototypes; each project is clearly labeled.
          </p>

          <ul
            className={
              "portfolio-menu nav nav-tabs fw-600 justify-content-start justify-content-md-center border-bottom-0 mb-5 wow fadeInUp"
            }
          >
            <li className="nav-item">
              <button
                className={"nav-link " + (filterKey === "*" ? "active" : "")}
                onClick={handleFilterKeyChange("*")}
              >
                All
              </button>
            </li>
            {Object.keys(filters).map((oneKey, i) => (
              <li className="nav-item" key={i}>
                <button
                  className={"nav-link " + (filterKey === oneKey ? "active" : "")}
                  onClick={handleFilterKeyChange(oneKey)}
                >
                  {filters[oneKey]}
                </button>
              </li>
            ))}
          </ul>

          <div className="portfolio wow fadeInUp">
            <div className="row portfolio-filter filter-container g-4">
              {projectsData.length > 0 &&
                projectsData.map((project, index) => (
                  <div
                    className={
                      "col-sm-6 col-lg-4 filter-item " + getFilterClasses(project.categories)
                    }
                    key={index}
                  >
                    <div className="portfolio-box">
                      <div
                        className={`portfolio-img ${
                          project.type === types.DOCUMENT && project.previewLayout
                            ? `portfolio-img--${project.previewLayout}`
                            : ""
                        }`}
                      >
                        <img
                          onLoad={() => {
                            setImagesLoaded((prev) => prev + 1);
                          }}
                          className={`img-fluid d-block portfolio-image ${
                            project.type === types.DOCUMENT && project.previewLayout
                              ? `portfolio-image--${project.previewLayout}`
                              : ""
                          }`}
                          src={project.thumbImage}
                          alt={project.title}
                        />
                        <div
                          className="portfolio-overlay"
                          onClick={() => {
                            openProjectModal(project, true);
                          }}
                        >
                          <button className="popup-ajax stretched-link border-0 p-0 "> </button>
                          <div className="portfolio-overlay-details">
                            <p className="text-primary text-8">
                              {project.type === types.DOCUMENT && <i className="fas fa-file-alt"></i>}
                              {project.type === types.IMAGE && <i className="fas fa-image"></i>}
                              {project.type === types.VIDEO && <i className="fas fa-video"></i>}
                            </p>
                            <h5 className="text-white text-5">{project?.title}</h5>
                            {project.status && <span className="text-light">{project.status}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      {isOpen && (
        <ProjectDetailsModal
          projectDetails={selectedProjectDetails}
          onClose={closeProjectModal}
        ></ProjectDetailsModal>
      )}
    </>
  );
};

export default Portfolio;
